import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsuiteApi } from '../../../api/testsuite';
import Navbar from '../../layout/Navbar';

function EditTestSuite() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    suite_name: '',
    description: '',
    base_url: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const fetchTestSuite = async () => {
      try {
        setLoading(true);
        const response = await testsuiteApi.getTestSuiteById(id);
        
        setFormData({
          suite_name: response.suite_name || '',
          description: response.description || '',
          base_url: response.url || ''
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching test suite:', error);
        setError('Failed to load test suite details');
        setLoading(false);
        
        if (error.message.includes('session has expired')) {
          navigate('/login');
        }
      }
    };

    fetchTestSuite();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Transform formData to match the API's expected format
      const apiData = {
        suite_name: formData.suite_name,
        description: formData.description,
        url: formData.base_url
      };
      
      await testsuiteApi.updateTestSuite(id, apiData);
      
      setSuccessMessage('Test suite updated successfully');
      setSaving(false);
      
      // Redirect after a short delay to show the success message
      setTimeout(() => {
        navigate('/testsuites');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating test suite:', error);
      setError(`Failed to update test suite: ${error.message}`);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/testsuites');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading test suite details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Edit Test Suite</h1>
            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Cancel
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
            <div className="mb-4">
              <label htmlFor="suite_name" className="block text-gray-700 text-sm font-bold mb-2">
                Test Suite Name *
              </label>
              <input
                type="text"
                id="suite_name"
                name="suite_name"
                value={formData.suite_name}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="base_url" className="block text-gray-700 text-sm font-bold mb-2">
                Base URL
              </label>
              <input
                type="url"
                id="base_url"
                name="base_url"
                value={formData.base_url}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="https://example.com"
              />
              <p className="text-gray-500 text-xs mt-1">
                The base URL for all tests in this suite. Individual tests can override this.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`bg-black hover:bg-gray-800 text-white font-bold py-2 px-6 rounded transition-colors ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditTestSuite;
